/**
 * Advanced Interest Score System for Edinburgh Planning Applications
 * Focuses on planning significance, community impact, and architectural merit
 * Version 2.0 - Completely refactored for better accuracy
 */

interface Application {
  reference_no: string;
  description: string;
  ai_summary?: string;
  scale_rating?: number;
  received_date?: string;
  validated_date?: string;
  address?: string;
  status?: string;
}

// High-impact development types that always generate community interest
const MAJOR_DEVELOPMENT_TYPES = {
  // Large-scale residential (housing crisis relevance)
  'student accommodation': 35,
  'student housing': 35,
  'pbsa': 35,
  'purpose built student accommodation': 40,
  'co-living': 30,
  'build to rent': 28,
  'rental development': 25,
  'affordable housing': 30,
  'social housing': 32,
  'housing association': 28,
  'residential development': 25,
  'apartment complex': 25,
  'residential block': 25,
  
  // Hotels & hospitality (tourism impact)
  'hotel': 30,
  'boutique hotel': 35,
  'apart-hotel': 28,
  'aparthotel': 28,
  'guest house': 20,
  'hostel': 25,
  'short term let': 15,
  'airbnb': 12,
  
  // Major commercial & infrastructure
  'data centre': 40,
  'data center': 40,
  'distribution centre': 35,
  'distribution center': 35,
  'warehouse': 25,
  'logistics hub': 30,
  'fulfillment centre': 30,
  'retail park': 30,
  'shopping centre': 35,
  'shopping center': 35,
  'supermarket': 25,
  'hypermarket': 30,
  
  // Entertainment & culture
  'cinema': 25,
  'multiplex': 30,
  'theatre': 35,
  'concert hall': 40,
  'music venue': 25,
  'nightclub': 20,
  'casino': 25,
  'bingo hall': 15,
  
  // Community facilities
  'school': 35,
  'primary school': 30,
  'secondary school': 35,
  'college': 40,
  'university': 45,
  'hospital': 45,
  'medical centre': 25,
  'health centre': 25,
  'care home': 30,
  'nursing home': 30,
  'community centre': 25,
  'library': 25,
  'sports centre': 25,
  'leisure centre': 30,
  'swimming pool': 25,
  'gym': 15,
  
  // Religious & cultural
  'mosque': 25,
  'church': 20,
  'synagogue': 25,
  'temple': 25,
  'community hall': 20,
  'cultural centre': 25,
  'museum': 35,
  'gallery': 25,
  
  // Industrial & employment
  'office building': 20,
  'office development': 25,
  'business park': 25,
  'industrial unit': 15,
  'factory': 20,
  'manufacturing': 25,
  'research facility': 30,
  'laboratory': 25
};

// Scale and size indicators - focus on community impact
const SCALE_IMPACT_INDICATORS = {
  // Height (visual impact)
  'high-rise': 35,
  'tower': 30,
  'skyscraper': 40,
  
  // Size descriptors
  'major development': 30,
  'large-scale': 25,
  'substantial': 20,
  'significant': 15,
  'extensive': 18,
  'comprehensive': 15,
  'massive': 25,
  'landmark': 30,
  'flagship': 25,
  
  // Multi-unit developments
  'multi-unit': 20,
  'multiple dwellings': 25,
  'apartment building': 20,
  'residential complex': 25,
  
  // Mixed-use (complexity)
  'mixed-use': 25,
  'mixed use': 25,
  'multi-purpose': 20,
  'integrated development': 25
};

// Controversial or high-impact changes
const HIGH_IMPACT_CHANGES = {
  // Demolition (heritage/community concern)
  'demolition': 25,
  'substantial demolition': 35,
  'complete demolition': 40,
  'demolish': 25,
  'tear down': 30,
  'clear site': 30,
  
  // Major alterations
  'change of use': 20,
  'conversion': 15,
  'transformation': 20,
  'redevelopment': 25,
  'comprehensive refurbishment': 20,
  
  // New construction
  'new build': 20,
  'new-build': 20,
  'new construction': 20,
  'ground-up': 25,
  'greenfield': 30,
  'brownfield': 20
};

// Heritage and conservation significance
const HERITAGE_SIGNIFICANCE = {
  // Statutory designations
  'category a listed': 40,
  'category b listed': 30,
  'category c listed': 20,
  'grade i': 45,
  'grade ii*': 35,
  'grade ii': 25,
  'scheduled monument': 40,
  'ancient monument': 35,
  'world heritage site': 50,
  'unesco': 45,
  
  // Conservation areas
  'conservation area': 20,
  'historic environment': 15,
  'new town conservation area': 25,
  'old town conservation area': 30,
  
  // Heritage elements
  'listed building consent': 15,
  'lbc': 12,
  'historic building': 15,
  'heritage asset': 18,
  'historic fabric': 20,
  'original features': 12,
  'period features': 10,
  'traditional materials': 8,
  'stone restoration': 10,
  'sash windows': 5
};

// Modern sustainability and innovation
const SUSTAINABILITY_INNOVATION = {
  // Climate targets
  'net zero': 25,
  'carbon neutral': 20,
  'carbon negative': 25,
  'zero carbon': 25,
  'climate positive': 30,
  
  // Building standards
  'passivhaus': 25,
  'passive house': 25,
  'breeam excellent': 20,
  'breeam outstanding': 25,
  'leed': 20,
  'well building': 20,
  
  // Green technology
  'renewable energy': 15,
  'solar panels': 10,
  'solar farm': 25,
  'wind turbine': 20,
  'geothermal': 15,
  'heat pump': 8,
  'battery storage': 12,
  'ev charging': 8,
  'electric vehicle charging': 10,
  
  // Green infrastructure
  'green roof': 15,
  'living roof': 15,
  'green wall': 12,
  'living wall': 12,
  'biodiversity': 12,
  'rewilding': 15,
  'urban forest': 20,
  'sustainable drainage': 10,
  'suds': 8,
  'rain garden': 8,
  
  // Innovative construction
  'modular construction': 12,
  'prefabricated': 10,
  'cross-laminated timber': 15,
  'clt': 15,
  'mass timber': 15,
  'innovative materials': 15,
  'smart building': 15,
  'digital twin': 20
};

// Location-based significance (Edinburgh context)
const LOCATION_SIGNIFICANCE = {
  // Prime city centre
  'princes street': 25,
  'royal mile': 30,
  'grassmarket': 20,
  'lawnmarket': 25,
  'canongate': 20,
  'high street': 15,
  'george street': 20,
  'queen street': 15,
  'rose street': 15,
  
  // Historic areas
  'new town': 20,
  'old town': 25,
  'calton hill': 15,
  'holyrood': 20,
  'arthur\'s seat': 15,
  
  // Developing areas
  'leith': 12,
  'granton': 15,
  'fountainbridge': 12,
  'haymarket': 15,
  'quartermile': 15,
  'edinburgh park': 10,
  
  // Residential areas of note
  'stockbridge': 10,
  'morningside': 8,
  'bruntsfield': 8,
  'marchmont': 8,
  'newington': 8
};

// Routine/low-interest applications (penalties)
const ROUTINE_APPLICATIONS = {
  // Advertisements and signage
  'advertisement': -15,
  'advertising': -12,
  'signage': -10,
  'sign': -8,
  'fascia sign': -12,
  'projecting sign': -10,
  'illuminated sign': -8,
  'non-illuminated sign': -6,
  'banner': -10,
  'flag': -8,
  
  // Minor alterations
  'internal alterations': -12,
  'minor alterations': -15,
  'minor works': -12,
  'routine maintenance': -15,
  'repair': -8,
  'repainting': -10,
  'painting': -8,
  'redecoration': -10,
  'like-for-like replacement': -12,
  'like for like': -10,
  
  // Window/door replacements
  'window replacement': -15,
  'door replacement': -10,
  'upvc windows': -12,
  'upvc doors': -10,
  'double glazing': -8,
  'replacement glazing': -10,
  
  // Minor extensions
  'small extension': -5,
  'minor extension': -8,
  'single storey rear extension': -6,
  'conservatory': -8,
  'porch': -6,
  'canopy': -5,
  
  // Routine commercial
  'shopfront': -8,
  'shop front': -8,
  'internal fit-out': -6,
  'change of tenant': -10,
  'temporary': -5,
  'meanwhile use': -3
};

// NEW HARSH penalties for extremely routine advertisement & small domestic works
const SUPER_ROUTINE_APPLICATIONS: Record<string, number> = {
  // Ultra-mundane adverts / certificates / minor household jobs
  'fascia sign': -40,
  'advertisement of the following types': -40,
  'advertisement of the following': -35,
  'non-material variation': -30,
  'non material variation': -30,
  'rear extension': -25,
  'single storey extension': -25,
  'small rear extension': -30,
  'lounge extension': -25,
  'driveway': -20,
  'access ramp': -25,
  'bin store': -20,
  'bike shed': -20,
  'chiropractic clinic': -20,
  'change of signage': -35,
};

// Application type significance
const APPLICATION_TYPE_WEIGHTS = {
  'FUL': 1.0,        // Full planning permission
  'PAN': 1.5,        // Proposal of Application Notice (major developments)
  'PPP': 1.3,        // Planning Permission in Principle
  'AMC': 1.2,        // Approval of Matters Specified in Conditions
  'LBC': 0.7,        // Listed Building Consent (often minor)
  'CAC': 0.8,        // Conservation Area Consent
  'ADV': 0.2,        // Advertisement (usually low interest)
  'VARY': 0.6,       // Variation (modification to existing)
  'CLP': 0.5,        // Certificate of Lawful Development
  'FULSTL': 0.3,     // Short term let (routine)
  'WINPA': 0.1,      // Window/door replacement (very routine)
  'DEM': 0.8,        // Demolition
  'PREAPP': 0.4      // Pre-application advice
};

/**
 * Advanced scoring algorithm that considers multiple factors
 */
export function getInterestScore(app: Application): number {
  let score = 0;
  
  // Combine all searchable text
  const searchText = `${app.description || ''} ${app.ai_summary || ''} ${app.address || ''}`.toLowerCase();
  
  // 1. Base score from API scale rating (if available)
  if (app.scale_rating && app.scale_rating > 0) {
    score += app.scale_rating * 6; // Scale up to make it more significant
  }
  
  // 2. Application type weighting
  const appType = app.reference_no?.split('/')[2] || 'FUL';
  const typeWeight = APPLICATION_TYPE_WEIGHTS[appType] || 1.0;
  
  // 3. Major development types (high community impact)
  let developmentScore = 0;
  Object.entries(MAJOR_DEVELOPMENT_TYPES).forEach(([keyword, points]) => {
    if (searchText.includes(keyword)) {
      developmentScore += points;
    }
  });
  
  // 4. Scale and impact indicators
  let scaleScore = 0;
  Object.entries(SCALE_IMPACT_INDICATORS).forEach(([keyword, points]) => {
    if (searchText.includes(keyword)) {
      scaleScore += points;
    }
  });
  
  // 5. High-impact changes
  let changeScore = 0;
  Object.entries(HIGH_IMPACT_CHANGES).forEach(([keyword, points]) => {
    if (searchText.includes(keyword)) {
      changeScore += points;
    }
  });
  
  // 6. Heritage significance
  let heritageScore = 0;
  Object.entries(HERITAGE_SIGNIFICANCE).forEach(([keyword, points]) => {
    if (searchText.includes(keyword)) {
      heritageScore += points;
    }
  });
  
  // 7. Sustainability and innovation
  let sustainabilityScore = 0;
  Object.entries(SUSTAINABILITY_INNOVATION).forEach(([keyword, points]) => {
    if (searchText.includes(keyword)) {
      sustainabilityScore += points;
    }
  });
  
  // 8. Location significance
  let locationScore = 0;
  Object.entries(LOCATION_SIGNIFICANCE).forEach(([keyword, points]) => {
    if (searchText.includes(keyword)) {
      locationScore += points;
    }
  });
  
  // 9. Routine application penalties
  let routineScore = 0;
  Object.entries(ROUTINE_APPLICATIONS).forEach(([keyword, points]) => {
    if (searchText.includes(keyword)) {
      routineScore += points; // These are negative
    }
  });
  
  // 9b. Super-routine application harsh penalties
  let superRoutineScore = 0;
  Object.entries(SUPER_ROUTINE_APPLICATIONS).forEach(([keyword, points]) => {
    if (searchText.includes(keyword)) {
      superRoutineScore += points; // Negative values
    }
  });
  
  // 10. Advanced pattern recognition
  
  // Multi-storey buildings (height impact)
  const storeyMatch = searchText.match(/(\d+)[-\s]*storey/i);
  if (storeyMatch) {
    const storeys = parseInt(storeyMatch[1]);
    if (storeys >= 6) {
      scaleScore += Math.min(storeys * 4, 40); // Significant bonus for tall buildings
    } else if (storeys >= 3) {
      scaleScore += storeys * 2; // Moderate bonus for medium height
    }
  }
  
  // Unit count (residential impact)
  const unitMatch = searchText.match(/(\d+)[-\s]*(units?|flats?|apartments?|dwellings?|homes?)/i);
  if (unitMatch) {
    const units = parseInt(unitMatch[1]);
    if (units >= 50) {
      developmentScore += 25; // Major residential development
    } else if (units >= 20) {
      developmentScore += 15; // Significant residential
    } else if (units >= 10) {
      developmentScore += 8; // Notable residential
    }
  }
  
  // Floor area (scale indicator)
  const areaMatch = searchText.match(/(\d+)[-\s]*(sqm|sq\.?m|square metres?)/i);
  if (areaMatch) {
    const area = parseInt(areaMatch[1]);
    if (area >= 5000) {
      scaleScore += 20; // Very large development
    } else if (area >= 2000) {
      scaleScore += 12; // Large development
    } else if (area >= 1000) {
      scaleScore += 6; // Medium development
    }
  }
  
  // Public consultation indicators (controversy/significance)
  if (searchText.includes('consultation') || searchText.includes('objection') || 
      searchText.includes('community engagement') || searchText.includes('public meeting')) {
    score += 15;
  }
  
  // Planning committee referral (significance)
  if (searchText.includes('committee') || searchText.includes('delegated') === false) {
    score += 10;
  }
  
  // Environmental impact
  if (searchText.includes('environmental impact') || searchText.includes('eia') || 
      searchText.includes('environmental statement')) {
    score += 20;
  }
  
  // 11. Combine all scores with type weighting
  const rawScore = (developmentScore + scaleScore + changeScore + heritageScore + 
                   sustainabilityScore + locationScore + routineScore + superRoutineScore) * typeWeight;
  
  score += rawScore;
  
  // 12. Final adjustments
  
  // Boost for detailed descriptions (indicates complexity)
  if (searchText.length > 200) {
    score += 5;
  }
  if (searchText.length > 500) {
    score += 5;
  }
  
  // Penalty for very short descriptions (likely routine)
  if (searchText.length < 50) {
    score -= 10;
  }
  
  // Boost for recent applications (current relevance)
  if (app.validated_date || app.received_date) {
    const rawDate = (app.validated_date || app.received_date) as string | number | Date | undefined;
    const appDate = new Date(rawDate as string | number | Date);
    const daysSince = Math.floor((Date.now() - appDate.getTime()) / (1000 * 60 * 60 * 24));
    if (daysSince <= 7) {
      score += 8; // Recent applications more interesting
    } else if (daysSince <= 30) {
      score += 4;
    }
  }
  
  // 13. Ensure reasonable bounds and return
  return Math.max(0, Math.min(150, Math.round(score)));
}

/**
 * Get detailed score breakdown for debugging and transparency
 */
export function getScoreBreakdown(app: Application): Record<string, number> {
  const searchText = `${app.description || ''} ${app.ai_summary || ''} ${app.address || ''}`.toLowerCase();
  const appType = app.reference_no?.split('/')[2] || 'FUL';
  
  // Calculate individual component scores
  const scaleRating = (app.scale_rating || 0) * 6;
  
  const developmentScore = Object.entries(MAJOR_DEVELOPMENT_TYPES)
    .filter(([keyword]) => searchText.includes(keyword))
    .reduce((sum, [, points]) => sum + points, 0);
    
  const scaleScore = Object.entries(SCALE_IMPACT_INDICATORS)
    .filter(([keyword]) => searchText.includes(keyword))
    .reduce((sum, [, points]) => sum + points, 0);
    
  const changeScore = Object.entries(HIGH_IMPACT_CHANGES)
    .filter(([keyword]) => searchText.includes(keyword))
    .reduce((sum, [, points]) => sum + points, 0);
    
  const heritageScore = Object.entries(HERITAGE_SIGNIFICANCE)
    .filter(([keyword]) => searchText.includes(keyword))
    .reduce((sum, [, points]) => sum + points, 0);
    
  const sustainabilityScore = Object.entries(SUSTAINABILITY_INNOVATION)
    .filter(([keyword]) => searchText.includes(keyword))
    .reduce((sum, [, points]) => sum + points, 0);
    
  const locationScore = Object.entries(LOCATION_SIGNIFICANCE)
    .filter(([keyword]) => searchText.includes(keyword))
    .reduce((sum, [, points]) => sum + points, 0);
    
  const routineScore = Object.entries(ROUTINE_APPLICATIONS)
    .filter(([keyword]) => searchText.includes(keyword))
    .reduce((sum, [, points]) => sum + points, 0);
  
  const superRoutineScore = Object.entries(SUPER_ROUTINE_APPLICATIONS)
    .filter(([keyword]) => searchText.includes(keyword))
    .reduce((sum, [, points]) => sum + points, 0);
  
  const typeWeight = APPLICATION_TYPE_WEIGHTS[appType] || 1.0;
  
  return {
    'Scale Rating': scaleRating,
    'Development Type': Math.round(developmentScore * typeWeight),
    'Scale Impact': Math.round(scaleScore * typeWeight),
    'Change Impact': Math.round(changeScore * typeWeight),
    'Heritage': Math.round(heritageScore * typeWeight),
    'Sustainability': Math.round(sustainabilityScore * typeWeight),
    'Location': Math.round(locationScore * typeWeight),
    'Routine Penalty': Math.round(routineScore * typeWeight),
    'Super Routine Penalty': Math.round(superRoutineScore * typeWeight),
    'Type Weight': typeWeight,
    'Final Score': getInterestScore(app)
  };
} 